import { useState } from 'react';

import type { CleaningType, HousekeepingRoomStatus } from '../../../pages/housekeeping/types/housekeeping';
import { useDemarrerMenage } from '../../api/useActionsMenage';
import BottomSheet from '../../components/BottomSheet';
import Button from '../../components/Button';
import Pills from '../../components/Pills';
import Text from '../../components/Text';
import { libelleType, typesProposes } from '../../helpers/menage';
import { t } from '../../i18n/langue';

import ChampTexte from './ChampTexte';

/**
 * Commencer le ménage — règles de `TodoCard` du web.
 *
 * Le type proposé est celui de la chambre. Un agent qui s'écarte du type venu du
 * PMS doit dire pourquoi (5 caractères au moins, comme le web) ; qui affecte les
 * chambres choisit librement, parmi tous les types.
 */
const COMMENTAIRE_MIN = 5;

interface FeuilleDemarrerProps {
    chambre: HousekeepingRoomStatus;
    peutAffecter: boolean;
    onFermer: () => void;
    onDemarre: () => void;
}

interface ErreurServeur {
    response?: { data?: { message?: string } };
}

export default function FeuilleDemarrer({ chambre, peutAffecter, onFermer, onDemarre }: FeuilleDemarrerProps) {
    const demarrer = useDemarrerMenage();
    const [type, setType] = useState<CleaningType>(chambre.cleaning_type ?? 'recouche');
    const [commentaire, setCommentaire] = useState('');
    const [erreur, setErreur] = useState<string | null>(null);

    const typeAuto = chambre.cleaning_type_source === 'auto' ? chambre.cleaning_type : null;
    const commentaireRequis = typeAuto !== null && type !== typeAuto && !peutAffecter;
    const pret = !commentaireRequis || commentaire.trim().length >= COMMENTAIRE_MIN;
    const options = typesProposes(peutAffecter).map((valeur) => ({ valeur, libelle: libelleType(valeur) ?? valeur }));

    const confirmer = () => {
        setErreur(null);
        demarrer.mutate(
            {
                serviceId: chambre.service_id,
                cleaning_type: type,
                ...(commentaireRequis ? { override_comment: commentaire.trim() } : {}),
            },
            {
                onSuccess: onDemarre,
                onError: (e) =>
                    setErreur((e as ErreurServeur)?.response?.data?.message ?? t('menage.demarrer.echec')),
            },
        );
    };

    return (
        <BottomSheet ouverte onClose={onFermer} libelle={t('menage.demarrer.titre')} libelleFermeture={t('commun.fermer')}>
            <div className="flex flex-col gap-4">
                <Text variante="titre" as="h2">
                    {chambre.room_name}
                </Text>
                {/* Espace commun : pas de type à choisir, celui du jour est gardé. */}
                {!chambre.is_common_area && (
                    <Pills libelle={t('menage.demarrer.typeMenage')} options={options} valeur={type} onChange={setType} />
                )}
                {commentaireRequis && (
                    <ChampTexte
                        libelle={t('menage.demarrer.pourquoi')}
                        valeur={commentaire}
                        onChange={setCommentaire}
                        aide={t('menage.demarrer.caracteresMin', { n: COMMENTAIRE_MIN })}
                    />
                )}
                {erreur && (
                    <div role="alert">
                        <Text variante="petit" ton="danger">
                            {erreur}
                        </Text>
                    </div>
                )}
                <Button
                    libelle={t('menage.demarrer.commencer')}
                    pleineLargeur
                    disabled={!pret || demarrer.isLoading}
                    onPress={confirmer}
                />
            </div>
        </BottomSheet>
    );
}
